import React, { CSSProperties, useCallback, useRef, useState } from 'react';

import moment from 'moment';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { DataTableFilterMetaData } from 'primereact/datatable';
import { useClickOutside } from 'primereact/hooks';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tooltip } from 'primereact/tooltip';
import { TimelineScale } from 'types/interfaces';

const getSortIcon = (
  field: 'name' | 'personnelNumber' | 'none',
  currentSortBy: 'name' | 'personnelNumber' | 'none',
  direction: string
) => {
  if (field !== currentSortBy) return 'pi pi-sort-alt';
  if (field === 'name') {
    return direction === 'asc'
      ? 'pi pi-sort-alpha-down'
      : 'pi pi-sort-alpha-up';
  }
  if (field === 'personnelNumber') {
    return direction === 'asc'
      ? 'pi pi-sort-numeric-down'
      : 'pi pi-sort-numeric-up';
  }
  return 'pi pi-sort-alt';
};

type TimelineHeaderProps = {
  startOfMonth: moment.Moment;
  daysInMonth: number;
  sidebarWidth: number;
  filters: Record<string, DataTableFilterMetaData>;
  sortBy: 'name' | 'personnelNumber' | 'none';
  sortDirection: 'asc' | 'desc' | 'none';
  onFilterChange: (
    filter: 'name' | 'personnelNumber',
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onSortChange: (field: 'name' | 'personnelNumber') => void;
  timelineScale: TimelineScale;
  onScaleChange: (newScale: TimelineScale, date?: moment.Moment) => void;
  baseDate: moment.Moment;
  showOnlyFreeEmployees?: boolean;
  onToggleFreeEmployeesFilter?: () => void;
  excludeLongAbsences?: boolean;
  onToggleExcludeLongAbsences?: () => void;
  todayShiftOnly?: boolean;
  onToggleTodayShiftOnly?: () => void;
};

const TimelineHeader: React.FC<TimelineHeaderProps> = ({
  startOfMonth,
  daysInMonth,
  sidebarWidth,
  filters,
  sortBy,
  sortDirection,
  onFilterChange,
  onSortChange,
  onScaleChange,
  timelineScale,
  baseDate,
  showOnlyFreeEmployees = false,
  onToggleFreeEmployeesFilter,
  excludeLongAbsences = false,
  onToggleExcludeLongAbsences,
  todayShiftOnly = false,
  onToggleTodayShiftOnly
}) => {
  const filterPanelRef = useRef<OverlayPanel>(null);
  const sortPanelRef = useRef<OverlayPanel>(null);
  const activePanelRef = useRef<OverlayPanel | null>(null);
  const activePanelElRef = useRef<HTMLElement>(null);

  useClickOutside(activePanelElRef, () => {
    if (activePanelRef.current) {
      activePanelRef.current.hide();
      activePanelRef.current = null;
      (activePanelElRef as React.MutableRefObject<HTMLElement | null>).current = null;
    }
  });

  const hasActiveFilter = Object.values(filters).some(
    (f) => (f?.value ?? '') !== ''
  ) || showOnlyFreeEmployees || excludeLongAbsences || todayShiftOnly;
  
  // Формируем список активных фильтров для tooltip
  const getActiveFiltersTooltip = () => {
    const activeFilters: string[] = [];
    
    if (filters.name?.value) {
      activeFilters.push('Поиск по ФИО');
    }
    if (filters.personnelNumber?.value) {
      activeFilters.push('Поиск по таб. номеру');
    }
    if (showOnlyFreeEmployees) {
      activeFilters.push('Сотрудники без нарядов на сегодня');
    }
    if (todayShiftOnly) {
      activeFilters.push('Сегодня работают');
    }
    if (excludeLongAbsences) {
      activeFilters.push('Исключить длительные отсутствия');
    }
    
    if (activeFilters.length === 0) {
      return 'Фильтрация';
    }
    
    return `Фильтр активен:\n${activeFilters.join(',\n')}`;
  };
  
  
  const clearFilter = (field: 'name' | 'personnelNumber') => {
    const fakeEvent = {
      target: { value: '' }
    } as React.ChangeEvent<HTMLInputElement>;
    onFilterChange(field, fakeEvent);
  };

  // собираем массив дней и уникальных ISO-недель
  const days = Array.from({ length: daysInMonth }, (_, i) =>
    startOfMonth.clone().add(i, 'days')
  );
  const weeks = Array.from(new Set(days.map((d) => d.isoWeek())));

  // Generate days for the week of baseDate
  const startOfWeek = baseDate.clone().startOf('isoWeek');
  const daysOfWeek = Array.from({ length: 7 }, (_, i) =>
    startOfWeek.clone().add(i, 'days')
  );

  // Hour labels
  const weekScaleHours = ['', '04:00', '08:00', '12:00', '16:00', '20:00'];
  const dayScaleHours = Array.from(
    { length: 24 },
    (_, i) => String(i).padStart(2, '0') + ':00'
  );

  // Common styles for cells
  const weekCellCommonStyle = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };
  const dayCellFlex = { flex: 1 };
  const hourCellWeekStyle: CSSProperties = { flex: 6, textAlign: 'center' };
  const hourCellDayStyle: CSSProperties = { flex: 1, textAlign: 'center' };


  // Render header rows
  const renderMonthRow = () => (
    <div
      className="header-row header-row--month"
      onClick={() => onScaleChange(TimelineScale.MONTH)}
    >
      <span className="month-scale flex justify-content-center gap-1">
        {startOfMonth.format('MMMM YYYY')}
        {timelineScale !== TimelineScale.MONTH && (
          <>
            <i
              className="pi pi-search-minus zoom-icon"
              data-pr-tooltip="Уменьшить масштаб до месяца"
            />
            <Tooltip target=".zoom-icon" position="top" />
          </>
        )}
      </span>
    </div>
  );

  // Weeks row
  const renderWeeksRow = () => {
    const daysToCount =
      timelineScale === TimelineScale.MONTH ? days : daysOfWeek;
    return (
      <div className="header-row header-row--weeks">
        {weeks.map((w, idx) => {
          const cnt = daysToCount.filter((d) => d.isoWeek() === w).length;
          const label = cnt < 2 ? '…' : `Неделя ${w}`;
          const showIcon = timelineScale !== TimelineScale.WEEK;
          const iconClass =
            timelineScale === TimelineScale.MONTH
              ? 'pi-search-plus'
              : 'pi-search-minus';
          const tooltipText =
            timelineScale === TimelineScale.MONTH
              ? 'Увеличить масштаб до недели'
              : 'Уменьшить масштаб до недели';

          return (
            <div
              key={idx}
              className="week-cell flex justify-content-center gap-1"
              onClick={() =>
                onScaleChange(
                  TimelineScale.WEEK,
                  startOfMonth.clone().isoWeek(w)
                )
              }
              style={{ flex: cnt, ...weekCellCommonStyle }}
            >
              {label}
              {showIcon && (
                <>
                  <i
                    className={`pi ${iconClass} zoom-icon`}
                    data-pr-tooltip={tooltipText}
                  />
                  <Tooltip target=".zoom-icon" position="top" />
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Days row
  const renderDaysRow = () => {
    const iconClass =
      timelineScale === TimelineScale.WEEK ? 'pi-search-plus' : '';
    const tooltipText = 'Увеличить масштаб до дня';

    if (timelineScale === TimelineScale.MONTH) {
      return (
        <div className="header-row header-row--days">
          {days.map((d, idx) => (
            <div
              key={idx}
              className="day-cell"
              style={dayCellFlex}
              onClick={() => onScaleChange(TimelineScale.DAY, d)}
            >
              {d.format('DD')} ({d.format('dd')})
            </div>
          ))}
        </div>
      );
    }

    if (timelineScale === TimelineScale.WEEK) {
      return (
        <div className="header-row header-row--days">
          {daysOfWeek.map((d, idx) => (
            <div
              key={idx}
              className="day-cell flex justify-content-center gap-1"
              style={dayCellFlex}
              onClick={() => onScaleChange(TimelineScale.DAY, d)}
            >
              {d.format('DD')} ({d.format('dd')})
              <i
                className={`pi ${iconClass} zoom-icon`}
                data-pr-tooltip={tooltipText}
              />
              <Tooltip target=".zoom-icon" position="top" />
            </div>
          ))}
        </div>
      );
    }

    // DAY scale: single day, no icon
    return (
      <div className="header-row header-row--days">
        <div className="day-cell" style={dayCellFlex}>
          {baseDate.format('dddd, DD MMMM YYYY')}
        </div>
      </div>
    );
  };

  const renderHoursRow = () => {
    if (timelineScale === TimelineScale.WEEK) {
      return (
        <div className="header-row header-row--hours">
          {daysOfWeek.map((_, idx) => (
            <div key={idx} className="day-cell" style={dayCellFlex}>
              {weekScaleHours.map((h) => (
                <div key={h} className="hour-cell" style={hourCellWeekStyle}>
                  <span>{h}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }
    if (timelineScale === TimelineScale.DAY) {
      return (
        <div className="header-row header-row--hours">
          {dayScaleHours.map((h) => (
            <div key={h} className="dayScale hour-cell" style={hourCellDayStyle}>
              <span>{h}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="timeline-header">
      {/* Left sidebar controls */}
      <div
        className="header-controls-container"
        style={{ width: sidebarWidth }}
      >
        <h2>Сотрудники</h2>
        <Button
          icon={`pi ${hasActiveFilter ? 'pi-filter-fill' : 'pi-filter'}`}
          className={`${hasActiveFilter ? '' : 'p-button-text'} ${hasActiveFilter ? 'p-button-info' : ''}`}
          onClick={(e) => filterPanelRef.current?.toggle(e)}
          outlined
          severity={hasActiveFilter ? 'info' : 'secondary'}
          tooltip={getActiveFiltersTooltip()}
        />
        <Button
          icon={getSortIcon(sortBy, sortBy, sortDirection)}
          className="p-button-text"
          onClick={(e) => sortPanelRef.current?.toggle(e)}
          tooltip="Сортировка"
        />
        <OverlayPanel
          ref={filterPanelRef}
          dismissable={false}
          showCloseIcon
          onShow={() => {
            activePanelRef.current = filterPanelRef.current;
            (activePanelElRef as React.MutableRefObject<HTMLElement | null>).current =
              filterPanelRef.current?.getElement() ?? null;
          }}
          onHide={() => {
            activePanelRef.current = null;
            (activePanelElRef as React.MutableRefObject<HTMLElement | null>).current = null;
          }}
        >
          <div className="p-fluid calendarAssingment-timelineHeader">
            {/* Name filter */}
            <div className="p-field-timeline-header">
              <label htmlFor="nameFilter">Поиск по ФИО</label>
              <div className="filter-wrap">
                <InputText
                  id="nameFilter"
                  value={filters.name.value}
                  onChange={(e) => onFilterChange('name', e)}
                />
                {filters.name.value && (
                  <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-sm clear-btn"
                    onClick={() => clearFilter('name')}
                    tooltip="Очистить"
                    severity="danger"
                  />
                )}
              </div>
            </div>
            {/* Personnel number filter */}
            <div className="p-field-timeline-header">
              <label htmlFor="numberFilter">Поиск по таб. номеру</label>
              <div className="filter-wrap">
                <InputText
                  id="numberFilter"
                  value={filters.personnelNumber.value}
                  onChange={(e) => onFilterChange('personnelNumber', e)}
                />
                {filters.personnelNumber.value && (
                  <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-sm clear-btn"
                    onClick={() => clearFilter('personnelNumber')}
                    tooltip="Очистить"
                    severity="danger"
                  />
                )}
              </div>
            </div>
            {/* Free employees filter */}
            {onToggleFreeEmployeesFilter && (
              <div className="p-field-timeline-header p-field-timeline-header_custom">
                <div className="p-checkbox-container flex align-items-end">
                  <Checkbox
                    inputId="freeEmployeesFilter"
                    checked={showOnlyFreeEmployees}
                    onChange={onToggleFreeEmployeesFilter}
                  />
                  <label htmlFor="freeEmployeesFilter" className="ml-2 mb-0">
                    <i
                      id="freeEmployeesFilter-tooltip"
                      className="pi pi-question-circle ml-1 mr-2"
                      style={{ cursor: 'pointer' }}
                    />
                    <span className={showOnlyFreeEmployees ? 'text-primary font-semibold' : ''}>
                      Сотрудники без нарядов на сегодня
                    </span>
                  </label>
                  <Tooltip target="#freeEmployeesFilter-tooltip" content="Показывать только сотрудников, по которым на текущий момент не назначены наряды" />
                </div>
              </div>
            )}
            {/* Busy today employees filter */}
            {onToggleTodayShiftOnly && (
              <div className="p-field-timeline-header p-field-timeline-header_custom">
                <div className="p-checkbox-container flex align-items-end">
                  <Checkbox
                    inputId="todayShiftOnlyFilter"
                    checked={todayShiftOnly}
                    onChange={onToggleTodayShiftOnly}
                  />
                  <label htmlFor="todayShiftOnlyFilter" className="ml-2 mb-0">
                    <i
                      id="todayShiftOnlyFilter-tooltip"
                      className="pi pi-question-circle ml-1 mr-2"
                      style={{ cursor: 'pointer' }}
                    />
                    <span className={todayShiftOnly ? 'text-primary font-semibold' : ''}>
                      Сегодня работают
                    </span>
                  </label>
                  <Tooltip target="#todayShiftOnlyFilter-tooltip" content="Сотрудники, у которых в текущий день начинается рабочая смена" />
                </div>
              </div>
            )}
            {/* Long absent employees filter */}
            {onToggleExcludeLongAbsences && (
              <div className="p-field-timeline-header p-field-timeline-header_custom">
                <div className="p-checkbox-container flex align-items-end">
                  <Checkbox
                    inputId="excludeLongAbsencesFilter"
                    checked={excludeLongAbsences}
                    onChange={onToggleExcludeLongAbsences}
                  />
                  <label htmlFor="excludeLongAbsencesFilter" className="ml-2 mb-0">
                    <i
                      id="excludeLongAbsencesFilter-tooltip"
                      className="pi pi-question-circle ml-1 mr-2"
                      style={{ cursor: 'pointer' }}
                    />
                    <span className={excludeLongAbsences ? 'text-primary font-semibold' : ''}>
                      На длительном отсутствии
                    </span>
                  </label>
                  <Tooltip target="#excludeLongAbsencesFilter-tooltip" content="Исключить сотрудников, у которых отсутствие продолжается весь текущий месяц" />
                </div>
              </div>
            )}
          </div>
        </OverlayPanel>
        <OverlayPanel
          ref={sortPanelRef}
          dismissable={false}
          showCloseIcon
          onShow={() => {
            activePanelRef.current = sortPanelRef.current;
            (activePanelElRef as React.MutableRefObject<HTMLElement | null>).current =
              sortPanelRef.current?.getElement() ?? null;
          }}
          onHide={() => {
            activePanelRef.current = null;
            (activePanelElRef as React.MutableRefObject<HTMLElement | null>).current = null;
          }}
        >
          <div className="p-fluid">
            <div className="p-field-timeline-header">
              <Button
                label="Сортировать по ФИО"
                icon={getSortIcon('name', sortBy, sortDirection)}
                onClick={() => onSortChange('name')}
                className="p-button-text"
              />
            </div>
            <div className="p-field-timeline-header mb-0">
              <Button
                label="Сортировать по таб. номеру"
                icon={getSortIcon('personnelNumber', sortBy, sortDirection)}
                onClick={() => onSortChange('personnelNumber')}
                className="p-button-text"
              />
            </div>
          </div>
        </OverlayPanel>
      </div>
      <div className="header-content">
        {renderMonthRow()}
        {renderWeeksRow()}
        {renderDaysRow()}
        {renderHoursRow()}
      </div>
    </div>
  );
};

export default TimelineHeader;
